/**
 * Qual viagem o app está mostrando — **para esta conta, neste aparelho**.
 *
 * Uma conta pode participar de várias viagens, então "a viagem" deixou de ser
 * uma constante e virou uma escolha, que precisa sobreviver a fechar o app,
 * inclusive **sem rede**. Por isso o que fica guardado é a viagem inteira, como
 * o servidor a descreve: é o que permite a Home desenhar nome, destino, datas e
 * fase corretos antes de a lista chegar.
 *
 * ── O defeito que este arquivo passou a impedir ──────────────────────────────
 *
 * A chave era `nv:viagem-ativa`, e a limpeza do logout varre `nv:session:*` e
 * `nv:trip:*`. Ela não casava com nenhum dos dois: **sobrevivia ao logout e à
 * troca de conta**. Uma conta recém-criada abria mostrando a viagem que outra
 * conta tinha aberto naquele aparelho — com as datas dela — sem nunca ter
 * configurado nada. Conta e viagem são conceitos separados, e uma sobra local
 * havia virado configuração implícita.
 *
 * Duas defesas, porque uma só não basta:
 *
 *  1. a chave passou a ser `nv:trip:ativa`, dentro do prefixo que o logout e a
 *     troca de conta já varrem;
 *  2. o registro carrega o `userId` de quem o gravou, e a leitura **exige** que
 *     seja o mesmo. Storage não varrido (aba antiga, falha na limpeza, chave
 *     escrita por uma versão anterior) não vira viagem de outra pessoa.
 */

const CHAVE = 'nv:trip:ativa';

/** Chave da versão anterior. Lida só para ser apagada. */
const CHAVE_ANTIGA = 'nv:viagem-ativa';

export interface ViagemAtiva {
  id: string;
  name: string;
  primaryDestination: string;
  startDate: string;
  endDate: string;
  timezone: string;
  baseCurrency: string;
  /** Já há despesas ou acertos: a moeda não muda mais. */
  currencyLocked: boolean;
  status: 'upcoming' | 'active' | 'finished' | 'archived';
  role: 'owner' | 'admin' | 'collaborator' | 'participant';
  /** Nulos até alguém preencher: viagem nova não tem hospedagem. */
  accommodationName: string | null;
  accommodationAddress: string | null;
  /** Hospedagem como lugar (Places), para o pin do mapa. */
  accommodationPlaceId: string | null;
  accommodationLatitude: number | null;
  accommodationLongitude: number | null;
  accommodationCheckIn: string | null;
  accommodationCheckOut: string | null;
  accommodationNotes: string | null;
  /** Funcionalidades desligadas por quem administra (só chaves com false). */
  features: Record<string, boolean>;
}

interface Guardado extends ViagemAtiva {
  /** Conta que escolheu esta viagem. Sem ele, o registro não é usável. */
  userId: string;
}

/**
 * Só devolve o que está completo E é da conta que está perguntando.
 *
 * Um registro sem datas (gravado por uma versão anterior) é descartado em vez
 * de completado: uma Home sem datas teria de inventá-las, e inventar dado de
 * viagem é exatamente o que não pode acontecer.
 */
export function lerViagemAtiva(userId: string): ViagemAtiva | null {
  try {
    // Sobra da versão anterior: nunca é usada, e sai do aparelho na primeira
    // leitura em vez de ficar esperando um logout que talvez não venha.
    localStorage.removeItem(CHAVE_ANTIGA);

    const bruto = localStorage.getItem(CHAVE);
    if (!bruto || !userId) return null;

    const dados = JSON.parse(bruto) as Partial<Guardado>;
    if (dados.userId !== userId) return null;
    // Sem moeda também é registro incompleto: a viagem não tem padrão de moeda,
    // e inventar uma aqui seria mostrar R$ onde o grupo gasta em dólar.
    if (!dados.id || !dados.startDate || !dados.endDate || !dados.timezone || !dados.baseCurrency) return null;

    return {
      id: dados.id,
      name: dados.name ?? '',
      primaryDestination: dados.primaryDestination ?? '',
      startDate: dados.startDate,
      endDate: dados.endDate,
      timezone: dados.timezone,
      baseCurrency: dados.baseCurrency,
      currencyLocked: dados.currencyLocked ?? false,
      status: dados.status ?? 'upcoming',
      role: dados.role ?? 'participant',
      accommodationName: dados.accommodationName ?? null,
      accommodationAddress: dados.accommodationAddress ?? null,
      accommodationPlaceId: dados.accommodationPlaceId ?? null,
      accommodationLatitude: dados.accommodationLatitude ?? null,
      accommodationLongitude: dados.accommodationLongitude ?? null,
      accommodationCheckIn: dados.accommodationCheckIn ?? null,
      accommodationCheckOut: dados.accommodationCheckOut ?? null,
      accommodationNotes: dados.accommodationNotes ?? null,
      features: dados.features ?? {},
    };
  } catch {
    // Modo privado, storage bloqueado, JSON corrompido: nenhum deles é motivo
    // para o app não abrir.
    return null;
  }
}

export function guardarViagemAtiva(userId: string, viagem: ViagemAtiva): void {
  if (!userId) return;
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ ...viagem, userId }));
  } catch {
    /* preferência de conforto: perder não quebra nada */
  }
}

export function esquecerViagemAtiva(): void {
  try {
    localStorage.removeItem(CHAVE);
    localStorage.removeItem(CHAVE_ANTIGA);
  } catch {
    /* idem */
  }
}
